import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { APIURL, TZ } from '../../../Global';
import axios from 'axios';
import './VendorRevenue.css';
import { useTranslation } from 'react-i18next';
import { formatDateString, formatDateForFilename } from '../../../utils/dateUtils';
import Modal from "react-bootstrap/Modal";
import { useAlert } from "react-alert";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import es from 'date-fns/locale/es';
import enUS from 'date-fns/locale/en-US';
const VendorDetails = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const vendorNameFromState = state && state.vendorName ? state.vendorName : '';
  const [dateFrom, setDateFrom] = useState(state?.dateFrom || '');
  const [dateTo, setDateTo] = useState(state?.dateTo || '');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [exportOpen, setExportOpen] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoiceImage, setSelectedInvoiceImage] = useState('');
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  registerLocale('es', es);
  registerLocale('en', enUS);
  const heading = useMemo(() => vendorNameFromState || t('vendorRev.vendorId', { id }), [vendorNameFromState, id, t]);

  useEffect(() => {
    if (!state?.dateFrom || !state?.dateTo) {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDayOfMonth.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo && id) {
      fetchDetails();
    }
  }, [id, dateFrom, dateTo, selectedFilter]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return t('vendorRev.invalidDate');
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);

      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('vendorRev.invalidDate');
    }
  }

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const tokenString = sessionStorage.getItem('usertoken');
      const str = tokenString ? tokenString.replace(/["]+/g, '') : '';
      // Build query params
      let queryParams = `start_date=${dateFrom}&end_date=${dateTo}&hospital_id=${id}&${TZ}`;

      // Add catalogue_type filter based on selection
      if (selectedFilter === 'products') {
        queryParams += `&catalogue_type=products`;
      } else if (selectedFilter === 'services') {
        queryParams += `&catalogue_type=services`;
      }

      const res = await axios.get(`${APIURL}/api/v1/staff/orders-list/?${queryParams}`, {
        headers: { Authorization: 'Token ' + str },
      });

      console.log('API Response:', res.data);

      if (res.data && res.data.status === 'success' && Array.isArray(res.data.orders)) {
        // Check if the detail array is empty
        if (res.data.orders.length === 0) {
          setItems([]);
          return;
        }

        // Transform API data to match expected format - only use the fields from response
        const transformedData = res.data.orders.map(item => ({
          order_number: item.order_number,
          catalogue_name: item.catalogue_name,
          catalogue_type: item.catalogue_type,
          date: formatDate(item.created_at),
          invoice_url: item.invoice_url,
          created_at: item.created_at
        }));

        setItems(transformedData);
      } else {
        // If no data or unexpected format, set empty array
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching vendor details:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceClick = async (invoiceUrl) => {
    if (!invoiceUrl) {
      console.warn('No invoice URL provided');
      return;
    }

    const userTokenString = sessionStorage.getItem("header_alegra");
    const userToken = userTokenString ? userTokenString.replace(/["]+/g, "") : "";

    if (!userToken) {
      alert.error(t('liabilities.loginAlegra'));
      return;
    }

    setLoadingInvoice(true);
    setShowInvoiceModal(true);

    try {
      // First try to get JSON response to check if it returns a PDF URL
      const jsonResponse = await axios.get(invoiceUrl, {
        headers: {
          Authorization: userToken,
        },
      });

      if (jsonResponse?.data?.pdf && typeof jsonResponse.data.pdf === "string" && jsonResponse.data.pdf.startsWith("http")) {
        setSelectedInvoiceImage(jsonResponse.data.pdf);
        return;
      }

      // If no PDF URL in JSON response, try to get the PDF blob directly
      const blobResponse = await axios.get(invoiceUrl, {
        headers: {
          Authorization: userToken,
        },
        responseType: "blob",
      });

      const contentType = blobResponse.headers["content-type"] || "application/pdf";
      const blob = new Blob([blobResponse.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);

      setSelectedInvoiceImage(blobUrl);
    } catch (err) {
      console.error("Error fetching invoice from Alegra:", err);
      // If all else fails, try to use the original URL directly
      setSelectedInvoiceImage(invoiceUrl);
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleCloseModal = () => {
    setShowInvoiceModal(false);

    // Clean up blob URL if it was created
    if (selectedInvoiceImage && selectedInvoiceImage.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(selectedInvoiceImage);
      } catch (e) {
        console.warn("Failed to revoke object URL", e);
      }
    }

    setSelectedInvoiceImage("");
  };

  const formatCSVField = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value).replace(/"/g, '""');
    if (/[",\n]/.test(str)) return `"${str}"`;
    return str;
  };

  const sanitizeCSV = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    return /^[=+\-@]/.test(str) ? `'${str}` : str;
  };

  const handleExportCSV = () => {
    if (!items || items.length === 0) return;
    const header = [t('vendorRev.orderNumber'), t('vendorRev.name'), t('vendorRev.catalogueType'), t('vendorRev.date')];
    const rows = items.map(r => [
      sanitizeCSV(r.order_number),
      sanitizeCSV(r.catalogue_name),
      sanitizeCSV(r.catalogue_type === 'products' ? t('vendorRev.product') : t('vendorRev.service')),
      sanitizeCSV(r.date)
    ]);
    const csvContent = [header, ...rows].map(r => r.map(formatCSVField).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendor_${id}_orders_${formatDateForFilename()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const style = `
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h1 { font-size: 18px; margin: 0 0 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f0f0f0; }
        .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
      </style>
    `;
    const thead = `<tr><th>${t('vendorRev.orderNumber')}</th><th>${t('vendorRev.name')}</th><th>${t('vendorRev.catalogueType')}</th><th>${t('vendorRev.date')}</th></tr>`;
    const tbody = items.map(r => `
      <tr>
        <td>${sanitizeCSV(r.order_number)}</td>
        <td>${sanitizeCSV(r.catalogue_name)}</td>
        <td>${sanitizeCSV(r.catalogue_type === 'products' ? t('vendorRev.product') : t('vendorRev.service'))}</td>
        <td>${sanitizeCSV(r.date)}</td>
      </tr>
    `).join('');
    const html = `
      <html>
        <head>
          <title>${heading}</title>
          ${style}
        </head>
        <body>
          <div class="header">
            <h1>${heading} - ${t('vendorRev.orderDetails')}</h1>
            <div>${t('vendorRev.hospitalId')}: ${id}</div>
          </div>
          <table>
            <thead>${thead}</thead>
            <tbody>${tbody}</tbody>
          </table>
        </body>
      </html>
    `;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  // Filter options
  const filterOptions = [
    { key: 'all', label: t('vendorRev.filters.all') },
    { key: 'products', label: t('vendorRev.filters.products') },
    { key: 'services', label: t('vendorRev.filters.services') }
  ];

  return (
    <div className="vendor-details-page" style={{ background: '#f6fafd', width: '100%', minHeight: '100vh', padding: '2%' }}>
      <div className="revenue-summary-section" style={{ marginBottom: 12 }}>
        <div className="revenue-summary-left" style={{ width: '100%' }}>
          {/* Header with title, export button, and back button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="revenue-summary-title" style={{ margin: 0 }}>{heading}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="export-dropdown">
                <button className="export-btn" onClick={() => setExportOpen(v => !v)}>
                  {t('vendorRev.filters.export')} ▼
                </button>
                {exportOpen && (
                  <div className="export-menu">
                    <button onClick={() => { handleExportCSV(); setExportOpen(false); }}>
                      {t('vendorRev.filters.exportCSV')}
                    </button>
                    <button onClick={() => { handleExportPDF(); setExportOpen(false); }}>
                      {t('vendorRev.filters.exportPDF')}
                    </button>
                  </div>
                )}
              </div>
              <button className="filter-btn" onClick={() => navigate(-1)}>{t('vendorRev.back')}</button>
            </div>
          </div>

          {/* Filters row */}
          <div className="revenue-filters" style={{ marginTop: 12 }}>
            <div className="date-filters" style={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
                <div className="date-filter-group1">
                  <label>{t('vendorRev.filters.fromDate')}</label>
                  <DatePicker
                    selected={dateFrom ? new Date(dateFrom) : null}
                    onChange={(date) => {
                      const formattedDate = date ? date.toISOString().split('T')[0] : '';
                      setDateFrom(formattedDate);
                      const today = new Date().toISOString().split('T')[0];
                      setDateTo(today);
                    }}
                    locale={i18n.language === 'es' ? 'es' : 'en'}
                    dateFormat="dd/MM/yyyy"
                    className="search-input1"
                    placeholderText="dd/mm/yyyy"
                    maxDate={new Date()}
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    showMonthDropdown
                    onChangeRaw={(e) => {
                      if (e.target && e.target.value) {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 2 && value.length <= 4) {
                          value = value.slice(0, 2) + '/' + value.slice(2);
                        } else if (value.length > 4 && value.length <= 8) {
                          value = value.slice(0, 2) + '/' + value.slice(2, 4) + '/' + value.slice(4);
                        }
                        e.target.value = value;
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Tab' ||
                        e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
                        return;
                      }
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  {/* <input
                    type="date"
                    className="search-input1"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  /> */}
                </div>
                <div className="date-filter-group1">
                  <label>{t('vendorRev.filters.toDate')}</label>
                  <DatePicker
                    selected={dateTo ? new Date(dateTo) : null}
                    onChange={(date) => {
                      const formattedDate = date ? date.toISOString().split('T')[0] : '';
                      setDateTo(formattedDate);
                    }}
                    locale={i18n.language === 'es' ? 'es' : 'en'}
                    dateFormat="dd/MM/yyyy"
                    className="search-input1"
                    placeholderText="dd/mm/yyyy"
                    maxDate={new Date()}
                    minDate={dateFrom ? new Date(dateFrom) : null}
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    showMonthDropdown
                    onChangeRaw={(e) => {
                      if (e.target && e.target.value) {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 2 && value.length <= 4) {
                          value = value.slice(0, 2) + '/' + value.slice(2);
                        } else if (value.length > 4 && value.length <= 8) {
                          value = value.slice(0, 2) + '/' + value.slice(2, 4) + '/' + value.slice(4);
                        }
                        e.target.value = value;
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Tab' ||
                        e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
                        return;
                      }
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  {/* <input
                    type="date"
                    className="search-input1"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  /> */}
                </div>
              </div>

              {/* Filter Buttons moved to right */}
              <div className="filter-buttons-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                {filterOptions.map(option => (
                  <button
                    key={option.key}
                    className={`filter-btn ${selectedFilter === option.key ? 'active' : ''}`}
                    onClick={() => setSelectedFilter(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="revenue-table-container" style={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div className="text-center py-5" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {t('vendorRev.loading')}
          </div>
        ) : items.length === 0 ? (
          <div className="no-data" style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            fontSize: '16px',
            color: '#666',
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            {t('vendorRev.noOrders')}
          </div>
        ) : (
          <>
            <div className="revenue-table-header revenue-grid" style={{ gridTemplateColumns: '20% 25% 20% 15% 20%' }}>
              <div><b>{t('vendorRev.orderNumber')}</b></div>
              <div><b>{t('vendorRev.name')}</b></div>
              <div><b>{t('vendorRev.catalogueType')}</b></div>
              <div><b>{t('vendorRev.date')}</b></div>
              <div><b>{t('vendorRev.invoice')}</b></div>
            </div>
            {items.map((row, idx) => (
              <div key={idx} className="revenue-table-row revenue-grid" style={{ gridTemplateColumns: '20% 25% 20% 15% 20%' }}>
                <div className="truncate" style={{ fontWeight: 600 }}>{row.order_number}</div>
                <div className="truncate">{row.catalogue_name}</div>
                <div>{row.catalogue_type === 'products' ? t('vendorRev.product') : t('vendorRev.service')}</div>
                <div>{formatDateString(row.date)}</div>
                <div>
                  {row.invoice_url && <button
                    className="view-btn"
                    onClick={() => handleInvoiceClick(row.invoice_url)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#01C9C9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                  >
                    {t('vendorRev.invoice')}
                  </button>}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <Modal show={showInvoiceModal} onHide={handleCloseModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('vendorRev.invoice')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: "center", minHeight: "400px", padding: 0 }}>
          {loadingInvoice ? (
            <div style={{ padding: "50px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">{t('vendorRev.loading')}</span>
              </div>
            </div>
          ) : selectedInvoiceImage ? (
            <iframe
              src={selectedInvoiceImage}
              title={t('vendorRev.invoice')}
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          ) : (
            <p style={{ padding: "50px" }}>{t('vendorRev.noInvoice')}</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default VendorDetails;